import type { QueryObserver } from '@tanstack/query-core';
import type { StoreApi } from 'zustand';

import { type AppError } from '../../error';
import { isDiff } from '../../shared/utils';

type BasicTModel = { _id: string };

export type StateRecord<T extends BasicTModel> = {
  data?: T;
  isLoading: boolean;
  hasError: AppError | undefined;
};

type BasicState<T extends BasicTModel> = {
  record: Record<string, StateRecord<T>>;
  asyncSubscriptions: Record<
    string,
    {
      observer: QueryObserver<any, any, any, any, any>;
    }
  >;

  // special scenarios for the listing
  isListLoading: StateRecord<T>['isLoading'];
  hasListError: StateRecord<T>['hasError'];

  computedCachedLists: {
    [key: string]: {
      data: T[];
      isLoading: boolean;
      hasError: AppError | undefined;
    };
  };
};
type BasicActions<TModel extends BasicTModel> = {
  // store related
  listFromStore: () => {
    isLoading: StateRecord<TModel>['isLoading'];
    hasError: StateRecord<TModel>['hasError'];
    data: StateRecord<TModel>['data'][];
  };
  getByIdFromStore: (id: string) => StateRecord<TModel>;
  getByIdsFromStore: (ids: string[]) => {
    isLoading: StateRecord<TModel>['isLoading'];
    hasError: StateRecord<TModel>['hasError'];
    data: StateRecord<TModel>['data'][];
  };

  updateToStore: (id: string, single: StateRecord<TModel>) => void;
  updateMultipleToStore: (
    single: TModel[],
    isListLoading: StateRecord<TModel>['isLoading'],
    hasListError: StateRecord<TModel>['hasError']
  ) => void;
  removeFromStore: (id: string) => string;
  clearStore: () => void;
};
type BasicFullState<TModel extends BasicTModel> = BasicState<TModel> & BasicActions<TModel>;

export function createBasicStoreState<TModel extends BasicTModel>(
  set: StoreApi<BasicFullState<TModel>>['setState'],
  get: StoreApi<BasicFullState<TModel>>['getState']
): BasicFullState<TModel> {
  const storeMethods: BasicActions<TModel> = {
    listFromStore: function () {
      const { record, computedCachedLists, isListLoading, hasListError } = get();

      let isLoading: StateRecord<TModel>['isLoading'] = isListLoading;
      let hasError: StateRecord<TModel>['hasError'] = hasListError;
      const list: TModel[] = [];

      const ids = Object.keys(record);
      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i];
        const data = record[id] ?? { isLoading: false, hasError: undefined };

        // check if any of the singles is loading or with error
        isLoading = isLoading ?? data.isLoading;
        hasError = hasError ?? data.hasError;
        if (data.data != null) {
          list.push(data.data);
        }
      }

      const res = {
        isLoading,
        hasError,
        data: list,
      };

      // check if we have a cached in
      const cachedId = 'listFromStore';
      const cached = computedCachedLists[cachedId] ?? undefined;
      if (cached != null && res.data.length === cached.data.length) {
        return cached;
      }

      set(function (_state) {
        return {
          computedCachedLists: {
            ...computedCachedLists,
            [cachedId]: res,
          },
        };
      });

      return res;
    },

    getByIdsFromStore: function (ids) {
      const { record, computedCachedLists, isListLoading, hasListError } = get();

      let isLoading: StateRecord<TModel>['isLoading'] = isListLoading;
      let hasError: StateRecord<TModel>['hasError'] = hasListError;
      const list: TModel[] = [];

      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i];
        const data = record[id];
        if (data?.data == null) {
          continue;
        }

        // check if any of the singles is loading or with error
        isLoading = isLoading ?? data.isLoading;
        hasError = hasError ?? data.hasError;
        list.push(data.data);
      }

      const res = {
        isLoading,
        hasError,
        data: list,
      };

      // check if we have a cached in
      const cachedId = `getByIdsFromStore_${ids.join(',')}`;
      const cached = computedCachedLists[cachedId] ?? undefined;
      if (cached != null && res.data.length === cached.data.length) {
        return cached;
      }

      set(function (_state) {
        return {
          computedCachedLists: {
            ...computedCachedLists,
            [cachedId]: res,
          },
        };
      });

      return res;
    },

    getByIdFromStore: function (id) {
      return get().record[id];
    },

    updateToStore: function (id, updated) {
      const { record } = get();
      const old = record[id];
      const is = isDiff(old, updated, ['_id']);

      // no point in updating store without anything to update
      if (!is) {
        return;
      }

      set(function (state) {
        // state needs to be immutable so if we need to update the record
        // we need to spread it and make it immutable
        return {
          computedCachedLists: {},
          record: {
            ...state.record,
            [id]: updated,
          },
        };
      });
    },

    updateMultipleToStore: function (list, isListLoading, hasListError) {
      if (isListLoading || hasListError != null) {
        set(function (_state) {
          return {
            isListLoading: isListLoading ?? false,
            hasListError: hasListError ?? undefined,
            computedCachedLists: {},
          };
        });

        return;
      }

      const { record } = get();

      const byId: typeof record = {};
      for (const single of list) {
        if (single == null) {
          continue;
        }

        const old = record[single._id]?.data;
        const is = isDiff(old, single, ['_id']);
        if (is) {
          byId[single._id] = {
            isLoading: false,
            hasError: undefined,
            data: single,
          };
        }
      }

      // no point in updating store without anything to update
      if (Object.keys(byId).length === 0) {
        return;
      }

      set(function (state) {
        // state needs to be immutable so if we need to update the record
        // we need to spread it and make it immutable
        return {
          computedCachedLists: {},
          record: {
            ...state.record,
            ...byId,
          },
        };
      });
    },

    removeFromStore: function (id) {
      // remove from the list
      set(function (state) {
        const newRecord = { ...state.record };
        delete newRecord[id];

        return {
          computedCachedLists: {},
          record: newRecord,
        };
      });

      return id;
    },

    clearStore: function () {
      set(function (_state) {
        return {
          computedCachedLists: {},
          record: {},
        };
      });
    },
  };

  return {
    ...storeMethods,
    record: {},
    computedCachedLists: {},
    asyncSubscriptions: {},

    // TODO: these might not be needed with the subscription
    isListLoading: false,
    hasListError: undefined,
  };
}
